import { useEffect, useRef, useState } from "react";
import { ChartLine, CheckSquare, ClipboardListIcon, Eye, ScrollText } from "lucide-react";
import { ChartSection } from "./ChartSection";
import { getApiErrorMessage } from "../../../../../../utils/utils";
import { isAbortedError } from "../../../../../../utils/global";

import {
    Principle,
    PrincipleChartData,
    ProjectOverview,
} from "../../core/_models";

import {
    getPrincipleChartData,
    getProjectOverviewData,
} from "../../core/_request";

import { useSnackbar } from "../../../../../../_theme/context/SnackbarContext";
import { LogError } from "../../../../../../utils/GlobalLogging";
import { useAuth } from "../../../../auth";
import { getStatusBadge, getStatusText } from "../../core/_utils";

interface OverviewProps {
    projectDescription?: string | null;
    evaluationPrinciples: Principle[];
    projectId: number;
    setEvalStatus: React.Dispatch<React.SetStateAction<string>>
}

const Overview = ({
    projectId,
    setEvalStatus
}: OverviewProps) => {
    const { showSnackbar } = useSnackbar();
    const { auth } = useAuth();
    const abortControllerRef = useRef<AbortController | null>(null);
    const [loading, setLoading] = useState(false);
    const [principleChartData, setPrincipleChartData] = useState<
        PrincipleChartData[]
    >([]);
    const [principleColors, setPrincipleColors] = useState<Record<string, string>>({});
    const [subPrincipleWeights, setSubPrincipleWeights] = useState<Record<string, number>>({});
    const [projectOverview, setProjectOverview] =
        useState<ProjectOverview | null>(null);

    const handleApiFailure = (
        message: string,
        error: unknown
    ) => {
        showSnackbar(getApiErrorMessage(error, message), "danger");
        LogError(message, error, "Overview");
    };

    const fetchData = async () => {
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setLoading(true);

        try {
            const [chartResult, detailsResult] =
                await Promise.allSettled([
                    getPrincipleChartData(
                        projectId,
                        controller.signal
                    ),
                    getProjectOverviewData(
                        projectId,
                        controller.signal
                    ),
                ]);

            if (controller.signal.aborted) return;

            // Chart data
            if (chartResult.status === "fulfilled") {
                const response = chartResult.value;

                if (response.status === "ok") {
                    setPrincipleChartData(
                        response.data.principle_chart_data ??
                        []
                    );
                    setPrincipleColors(
                        response.data.principle_colors ?? {}
                    );
                    setSubPrincipleWeights(
                        response.data.sub_principle_weights ?? {}
                    );
                } else {
                    showSnackbar(
                        response.message ||
                        "Failed to fetch chart data",
                        "danger"
                    );
                }
            } else if (!isAbortedError(chartResult.reason)) {
                handleApiFailure(
                    "Failed to fetch chart data",
                    chartResult.reason
                );
            }

            // Project overview
            if (detailsResult.status === "fulfilled") {
                const response = detailsResult.value;

                if (response.status === "ok") {
                    setProjectOverview(response.data);
                    setEvalStatus(getStatusText(response.data.eval_status || ""));
                } else {
                    showSnackbar(
                        response.message ||
                        "Failed to fetch project details",
                        "danger"
                    );
                }
            } else if (!isAbortedError(detailsResult.reason)) {
                handleApiFailure(
                    "Failed to fetch project details",
                    detailsResult.reason
                );
            }
        } catch (error) {
            if (isAbortedError(error)) return;

            handleApiFailure(
                "Failed to fetch overview data",
                error
            );
        } finally {
            if (!controller.signal.aborted) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
        return () => {
            abortControllerRef.current?.abort();
        };
    }, [projectId, showSnackbar]);

    return (
        <div>
            <div className="row g-2">

                {loading && (
                    <div className="card p-2 shadow-sm mb-2 border-0 bg-light">
                        <div className="d-flex align-items-center gap-2 text-primary small">
                            <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                            >
                                <span className="visually-hidden">
                                    Loading...
                                </span>
                            </div>
                            <span>Refreshing project data...</span>
                        </div>
                    </div>
                )}


                {auth?.isAdmin === 1 && <div className="col-12">
                    {/* Small inline loader */}

                    {/* Project Details */}
                    {/* <ProjectDetails
                        projectDetails={projectDescription || "-"}
                    /> */}

                    {/* Stats */}
                    <div className="row row-cols-2 row-cols-md-5 g-7">
                        {/* Average Score */}
                        <div className="col">
                            <div className="card p-3 shadow-sm border-0 border-start border-4 border-info h-100">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="bg-light-info p-2 rounded">
                                            <ScrollText size={20} className="text-info" />
                                        </div>

                                        <p className="fw-semibold text-gray-700 fs-6 mb-0">
                                            Average Score
                                        </p>
                                    </div>

                                    <div className="fs-2 text-gray-800 fw-bold">
                                        {projectOverview?.project_avg_score?.toFixed(1) ?? "0.0"}%
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Overall Status */}
                        <div className="col">
                            <div className="card p-3 shadow-sm border-0 border-start border-4 border-danger h-100">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="bg-light-danger p-2 rounded">
                                            <ChartLine size={20} className="text-danger" />
                                        </div>

                                        <p className="fw-semibold text-gray-700 fs-6 mb-0">
                                            Overall Status
                                        </p>
                                    </div>

                                    <span
                                        className={`badge px-3 py-2 fs-6 ${getStatusBadge(
                                            getStatusText(projectOverview?.eval_status)
                                        )} text-white`}
                                    >
                                        {getStatusText(projectOverview?.eval_status)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Evaluators Assigned */}
                        <div className="col">
                            <div className="card p-3 shadow-sm border-0 border-start border-4 border-primary h-100">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="bg-light-primary p-2 rounded">
                                            <ClipboardListIcon size={20} className="text-primary" />
                                        </div>

                                        <p className="fw-semibold text-gray-700 fs-6 mb-0">
                                            Evaluators Assigned
                                        </p>
                                    </div>

                                    <div className="fs-2 text-gray-800 fw-bold">
                                        {projectOverview?.total_evaluators ?? 0}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Evaluations Completed */}
                        <div className="col">
                            <div className="card p-3 shadow-sm border-0 border-start border-4 border-success h-100">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="bg-light-success p-2 rounded">
                                            <CheckSquare size={20} className="text-success" />
                                        </div>

                                        <p className="fw-semibold text-gray-700 fs-6 mb-0">
                                            Evaluations Completed
                                        </p>
                                    </div>

                                    <div className="fs-2 text-gray-800 fw-bold">
                                        {projectOverview?.evaluations_completed ?? 0}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Pending Evaluations */}
                        <div className="col">
                            <div className="card p-3 shadow-sm border-0 border-start border-4 border-warning h-100">
                                <div className="d-flex align-items-center justify-content-between gap-2">
                                    <div className="d-flex gap-3 align-items-center">
                                        <div className="bg-light-warning p-2 rounded">
                                            <Eye size={20} className="text-warning" />
                                        </div>

                                        <p className="fw-semibold text-gray-700 fs-6 mb-0">
                                            Pending Evaluations
                                        </p>
                                    </div>

                                    <div className="fs-2 text-gray-800 fw-bold">
                                        {projectOverview?.evaluation_pending ?? 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Evaluation Header */}
                    {/* <div className="card p-4 shadow-sm mb-2">
                        <p className="mb-0 fw-bold text-gray-700 fs-6">
                            Evaluation
                        </p>
                    </div> */}

                    {/* Evaluation Section */}
                    {/* <div className="card p-4 shadow-sm">
                        <Evaluation
                            evaluationPrinciples={
                                evaluationPrinciples ?? []
                            }
                        />
                    </div> */}
                </div>}
                <div className="col-12">
                    {/* Overall Status */}
                    {/* <div className="card p-3 shadow-sm mb-2">
                        <div className="d-flex justify-content-between align-items-center gap-2">
                            <p className="fw-semibold mb-0">
                                Overall Status :
                            </p>

                            <div
                                className={`badge ${getStatusBadge(
                                    projectOverview?.eval_status
                                )} text-white`}
                            >
                                {getStatusText(
                                    projectOverview?.eval_status
                                )}
                            </div>
                        </div>
                    </div> */}

                    {/* Chart Section */}
                    <ChartSection
                        principleChartData={principleChartData}
                        principleColors={principleColors}
                        subPrincipleWeights={subPrincipleWeights}
                        loading={loading}
                        projectId={projectId}
                    />
                </div>

            </div>
        </div >
    );
};

export { Overview };